const DEFAULT_TRANSLATE_ENDPOINT = process.env.REACT_APP_TRANSLATE_ENDPOINT
  || (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5005/translate'
    : '/api/translate');

const GOOGLE_TRANSLATE_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';
const TRANSLATE_TIMEOUT_MS = 1500;

const resolveWindow = () => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return undefined;
};

class TextTranslator {
  constructor() {
    this.cache = new Map();
    this.window = resolveWindow();
  }

  isSupported() {
    return typeof fetch !== 'undefined';
  }

  getCacheKey(text = '', targetLang = 'en', sourceLang = 'auto') {
    return `${sourceLang}|${targetLang}|${text}`;
  }

  normalizeLang(lang) {
    if (!lang) return 'en';
    return String(lang).split('-')[0].toLowerCase();
  }

  buildUrl(text, targetLang, sourceLang, endpointOverride) {
    const endpoint = endpointOverride || DEFAULT_TRANSLATE_ENDPOINT || GOOGLE_TRANSLATE_ENDPOINT;
    const normalizedTarget = this.normalizeLang(targetLang);
    const normalizedSource = sourceLang === 'auto' ? 'auto' : this.normalizeLang(sourceLang);

    if (endpoint === GOOGLE_TRANSLATE_ENDPOINT) {
      const params = new URLSearchParams({
        client: 'gtx',
        sl: normalizedSource,
        tl: normalizedTarget,
        dt: 't',
        q: text,
      });
      return `${endpoint}?${params.toString()}`;
    }

    const params = new URLSearchParams({
      text,
      target: normalizedTarget,
      source: normalizedSource,
    });
    return `${endpoint}?${params.toString()}`;
  }

  getEndpointCandidates() {
    const endpoints = [];
    if (DEFAULT_TRANSLATE_ENDPOINT) {
      endpoints.push(DEFAULT_TRANSLATE_ENDPOINT);
    }
    if (this.window?.location?.origin) {
      endpoints.push(`${this.window.location.origin}/translate`);
    }
    if (process.env.NODE_ENV === 'development') {
      endpoints.push('http://localhost:5005/translate');
    }
    endpoints.push(GOOGLE_TRANSLATE_ENDPOINT);
    return Array.from(new Set(endpoints.filter(Boolean)));
  }

  async translate(text, targetLang = 'en', sourceLang = 'auto') {
    if (!text || !targetLang || !this.isSupported()) {
      return text;
    }

    const key = this.getCacheKey(text, targetLang, sourceLang);
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    try {
      const endpoints = this.getEndpointCandidates();
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), TRANSLATE_TIMEOUT_MS);
          const url = this.buildUrl(text, targetLang, sourceLang, endpoint);

          const response = await fetch(url, { signal: controller.signal });
          clearTimeout(timeout);
          if (!response.ok) {
            throw new Error(`Translation failed: ${response.status}`);
          }

          const data = await response.json();
          const translated = Array.isArray(data)
            ? data[0].map(entry => entry[0]).join('')
            : (data?.translatedText || text);

          this.cache.set(key, translated);
          return translated;
        } catch (err) {
          lastError = err;
        }
      }

      throw lastError || new Error('Translation failed');
    } catch (error) {
      console.warn('Translation fallback triggered:', error);
      this.cache.set(key, text);
      return text;
    }
  }

  clearCacheForText(text) {
    if (!text) return;
    const entries = Array.from(this.cache.entries()).filter(([key]) => key.includes(text));
    entries.forEach(([key]) => this.cache.delete(key));
  }
}

const textTranslator = new TextTranslator();

if (resolveWindow()) {
  resolveWindow().textTranslator = textTranslator;
}

export default textTranslator;
